import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider
} from '@mui/material';
import {
  Backup as BackupIcon,
  Settings as SettingsIcon,
  CloudUpload as CloudUploadIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Restore as RestoreIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  getDropboxBackupSettings,
  DropboxBackupSettings,
  createDropboxBackup,
  getDropboxBackups,
  restoreDropboxBackup,
  DropboxBackup,
  downloadDatabaseFromLink,
  createDropboxShareLink
} from '../services/dropboxService';

const BackupPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // حالة الإعدادات
  const [dropboxSettings, setDropboxSettings] = useState<DropboxBackupSettings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDropboxConnected, setIsDropboxConnected] = useState<boolean>(false);

  // حالة النسخ الاحتياطية
  const [backups, setBackups] = useState<DropboxBackup[]>([]);
  const [loadingBackups, setLoadingBackups] = useState<boolean>(false);

  // حالة الحوارات
  const [createBackupDialog, setCreateBackupDialog] = useState<boolean>(false);
  const [restoreBackupDialog, setRestoreBackupDialog] = useState<boolean>(false);
  const [uploadFromLinkDialog, setUploadFromLinkDialog] = useState<boolean>(false);
  const [shareLinkDialog, setShareLinkDialog] = useState<boolean>(false);
  const [selectedBackup, setSelectedBackup] = useState<DropboxBackup | null>(null);
  const [backupName, setBackupName] = useState<string>('');
  const [dropboxLink, setDropboxLink] = useState<string>('');
  const [generatedLink, setGeneratedLink] = useState<string>('');

  // حالة الإشعارات
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // تحميل إعدادات Dropbox عند تحميل الصفحة
  useEffect(() => {
    loadDropboxSettings();
  }, []);

  // تحميل النسخ الاحتياطية عند الاتصال بـ Dropbox
  useEffect(() => {
    if (isDropboxConnected) {
      loadBackups();
    }
  }, [isDropboxConnected]);

  const loadDropboxSettings = async () => {
    try {
      setLoading(true);
      const settings = await getDropboxBackupSettings();
      setDropboxSettings(settings);
      setIsDropboxConnected(!!settings.accessToken);
    } catch (error) {
      console.error('Error loading dropbox settings:', error);
      setSnackbar({
        open: true,
        message: 'فشل في تحميل إعدادات Dropbox',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoToSettings = () => {
    navigate('/admin/advanced-settings');
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // تحميل النسخ الاحتياطية
  const loadBackups = async () => {
    try {
      setLoadingBackups(true);
      const backupsList = await getDropboxBackups();
      setBackups(backupsList);
    } catch (error) {
      console.error('Error loading backups:', error);
      setSnackbar({
        open: true,
        message: 'فشل في تحميل النسخ الاحتياطية',
        severity: 'error'
      });
    } finally {
      setLoadingBackups(false);
    }
  };

  // إنشاء نسخة احتياطية جديدة
  const handleCreateBackup = async () => {
    if (!backupName.trim()) {
      setSnackbar({
        open: true,
        message: 'يرجى إدخال اسم للنسخة الاحتياطية',
        severity: 'warning'
      });
      return;
    }

    try {
      setLoadingBackups(true);

      // إنشاء النسخة الاحتياطية (ستقوم الدالة بجمع البيانات تلقائياً)
      await createDropboxBackup(backupName.trim());
      setSnackbar({
        open: true,
        message: 'تم إنشاء النسخة الاحتياطية بنجاح',
        severity: 'success'
      });
      setCreateBackupDialog(false);
      setBackupName('');
      loadBackups(); // إعادة تحميل القائمة
    } catch (error) {
      console.error('Error creating backup:', error);
      setSnackbar({
        open: true,
        message: 'فشل في إنشاء النسخة الاحتياطية',
        severity: 'error'
      });
    } finally {
      setLoadingBackups(false);
    }
  };

  // استعادة نسخة احتياطية
  const handleRestoreBackup = async () => {
    if (!selectedBackup) return;

    try {
      setLoadingBackups(true);
      await restoreDropboxBackup(selectedBackup.id);
      setSnackbar({
        open: true,
        message: 'تم استعادة النسخة الاحتياطية بنجاح',
        severity: 'success'
      });
      setRestoreBackupDialog(false);
      setSelectedBackup(null);
    } catch (error) {
      console.error('Error restoring backup:', error);
      setSnackbar({
        open: true,
        message: 'فشل في استعادة النسخة الاحتياطية',
        severity: 'error'
      });
    } finally {
      setLoadingBackups(false);
    }
  };

  // تحميل قاعدة البيانات من رابط Dropbox
  const handleUploadFromLink = async () => {
    if (!dropboxLink.trim()) {
      setSnackbar({
        open: true,
        message: 'يرجى إدخال رابط Dropbox',
        severity: 'warning'
      });
      return;
    }

    try {
      setLoadingBackups(true);

      // تحميل البيانات من الرابط
      const backupData = await downloadDatabaseFromLink(dropboxLink.trim());

      // استعادة البيانات (يمكن إضافة منطق الاستعادة هنا)
      console.log('Downloaded backup data:', backupData);

      setSnackbar({
        open: true,
        message: 'تم تحميل قاعدة البيانات بنجاح',
        severity: 'success'
      });
      setUploadFromLinkDialog(false);
      setDropboxLink('');
    } catch (error) {
      console.error('Error uploading from link:', error);
      setSnackbar({
        open: true,
        message: 'فشل في تحميل قاعدة البيانات من الرابط',
        severity: 'error'
      });
    } finally {
      setLoadingBackups(false);
    }
  };

  // تنسيق حجم الملف
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // تنسيق التاريخ
  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date));
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <BackupIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            النسخ الاحتياطية
          </Typography>
        </Box>

        {!isDropboxConnected ? (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  <WarningIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  لم يتم تكوين Dropbox
                </Typography>
                <Typography variant="body1" paragraph>
                  لاستخدام ميزة النسخ الاحتياطية، يجب أولاً تكوين إعدادات Dropbox في الإعدادات المتقدمة.
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  ستحتاج إلى رمز الوصول (Access Token) من Dropbox لتفعيل هذه الميزة.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>خطوات الحصول على رمز الوصول:</strong>
                </Typography>
                <Box component="ol" sx={{ mt: 1, pl: 2, fontSize: '0.875rem' }}>
                  <li>انتقل إلى <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noopener noreferrer">Dropbox App Console</a></li>
                  <li>أنشئ تطبيق جديد أو استخدم تطبيق موجود</li>
                  <li>في إعدادات التطبيق، انتقل إلى تبويب "Settings"</li>
                  <li>في قسم "OAuth 2"، انقر على "Generate access token"</li>
                  <li>انسخ الرمز واستخدمه في الإعدادات المتقدمة</li>
                  <li><strong>مهم:</strong> تأكد من أن التطبيق له صلاحيات "files.content.read" و "sharing.write"</li>
                </Box>
              </Alert>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <SettingsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    تكوين Dropbox
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    قم بإعداد رمز الوصول الخاص بـ Dropbox لتفعيل النسخ الاحتياطية التلقائية.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SettingsIcon />}
                    onClick={handleGoToSettings}
                    fullWidth
                  >
                    الذهاب إلى الإعدادات المتقدمة
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <CloudUploadIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    ميزات النسخ الاحتياطي
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    بعد تكوين Dropbox، ستتمكن من:
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                    <li>إنشاء نسخ احتياطية يدوية</li>
                    <li>جدولة النسخ الاحتياطية التلقائية</li>
                    <li>استعادة البيانات من النسخ السابقة</li>
                    <li>إدارة النسخ الاحتياطية المحفوظة</li>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  تم تكوين Dropbox بنجاح!
                </Typography>
                <Typography variant="body1">
                  النظام متصل بـ Dropbox ويمكنك الآن استخدام ميزات النسخ الاحتياطي.
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    إعدادات Dropbox الحالية
                  </Typography>
                  {dropboxSettings && (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        <strong>المجلد:</strong> {dropboxSettings.folderName}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>الحد الأقصى للنسخ:</strong> {dropboxSettings.maxBackups}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>الرفع التلقائي:</strong> {dropboxSettings.autoUpload ? 'مفعل' : 'معطل'}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>الحذف التلقائي:</strong> {dropboxSettings.autoDelete ? 'مفعل' : 'معطل'}
                      </Typography>
                    </Box>
                  )}

                  <Box mt={2}>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<SettingsIcon />}
                      onClick={handleGoToSettings}
                    >
                      تعديل الإعدادات
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      إدارة النسخ الاحتياطية
                    </Typography>
                    <Box>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateBackupDialog(true)}
                        sx={{ mr: 1 }}
                        disabled={loadingBackups}
                      >
                        إنشاء نسخة احتياطية
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<UploadIcon />}
                        onClick={() => setUploadFromLinkDialog(true)}
                        sx={{ mr: 1 }}
                        disabled={loadingBackups}
                      >
                        تحميل من رابط
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadBackups}
                        disabled={loadingBackups}
                      >
                        تحديث
                      </Button>
                    </Box>
                  </Box>

                  {loadingBackups ? (
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress />
                    </Box>
                  ) : backups.length === 0 ? (
                    <Alert severity="info">
                      <Typography variant="body1">
                        لا توجد نسخ احتياطية حتى الآن. قم بإنشاء أول نسخة احتياطية.
                      </Typography>
                    </Alert>
                  ) : (
                    <List>
                      {backups.map((backup, index) => (
                        <React.Fragment key={backup.id}>
                          <ListItem>
                            <ListItemIcon>
                              <BackupIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary={backup.name}
                              secondary={
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    تاريخ الإنشاء: {formatDate(backup.createdAt)}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    الحجم: {formatFileSize(backup.size)}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    بواسطة: {backup.createdBy}
                                  </Typography>
                                </Box>
                              }
                            />
                            <Box>
                              <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<RestoreIcon />}
                                onClick={() => {
                                  setSelectedBackup(backup);
                                  setRestoreBackupDialog(true);
                                }}
                                sx={{ mr: 1 }}
                                disabled={loadingBackups}
                              >
                                استعادة
                              </Button>
                              <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<DownloadIcon />}
                                onClick={() => {
                                  if (backup.downloadUrl) {
                                    window.open(backup.downloadUrl, '_blank');
                                  }
                                }}
                                disabled={!backup.downloadUrl}
                                sx={{ mr: 1 }}
                              >
                                تحميل
                              </Button>
                              <Button
                                variant="outlined"
                                color="info"
                                startIcon={<LinkIcon />}
                                onClick={async () => {
                                  try {
                                    const settings = await getDropboxBackupSettings();
                                    if (settings.accessToken) {
                                      const shareLink = await createDropboxShareLink(
                                        `/${settings.folderName}/${backup.dropboxFileId}`,
                                        settings.accessToken
                                      );

                                      setGeneratedLink(shareLink);
                                      setSelectedBackup(backup);
                                      setShareLinkDialog(true);
                                    } else {
                                      setSnackbar({
                                        open: true,
                                        message: 'لم يتم تكوين Dropbox. يرجى إضافة رمز الوصول في الإعدادات',
                                        severity: 'warning'
                                      });
                                    }
                                  } catch (error) {
                                    console.error('Error creating share link:', error);
                                    setSnackbar({
                                      open: true,
                                      message: 'فشل في إنشاء رابط المشاركة. يرجى التحقق من إعدادات Dropbox',
                                      severity: 'error'
                                    });
                                  }
                                }}
                                disabled={loadingBackups}
                              >
                                رابط
                              </Button>
                            </Box>
                          </ListItem>
                          {index < backups.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* إشعارات */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* حوار إنشاء نسخة احتياطية */}
      <Dialog open={createBackupDialog} onClose={() => setCreateBackupDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>إنشاء نسخة احتياطية جديدة</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="اسم النسخة الاحتياطية"
            fullWidth
            variant="outlined"
            value={backupName}
            onChange={(e) => setBackupName(e.target.value)}
            placeholder="مثال: نسخة احتياطية - 2024"
            sx={{ mt: 2 }}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              سيتم إنشاء نسخة احتياطية شاملة تحتوي على جميع البيانات (العملاء، طلبات الصيانة، المخزون، إلخ).
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateBackupDialog(false)}>
            إلغاء
          </Button>
          <Button
            onClick={handleCreateBackup}
            variant="contained"
            disabled={!backupName.trim() || loadingBackups}
            startIcon={loadingBackups ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {loadingBackups ? 'جاري الإنشاء...' : 'إنشاء'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار استعادة نسخة احتياطية */}
      <Dialog open={restoreBackupDialog} onClose={() => setRestoreBackupDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>استعادة نسخة احتياطية</DialogTitle>
        <DialogContent>
          {selectedBackup && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>تحذير:</strong> ستؤدي هذه العملية إلى استبدال جميع البيانات الحالية!
                </Typography>
                <Typography variant="body2">
                  تأكد من أن هذا ما تريده قبل المتابعة. لا يمكن التراجع عن هذه العملية.
                </Typography>
              </Alert>

              <Typography variant="h6" gutterBottom>
                تفاصيل النسخة الاحتياطية:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="الاسم"
                    secondary={selectedBackup.name}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="تاريخ الإنشاء"
                    secondary={formatDate(selectedBackup.createdAt)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="الحجم"
                    secondary={formatFileSize(selectedBackup.size)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="منشئ النسخة"
                    secondary={selectedBackup.createdBy}
                  />
                </ListItem>
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreBackupDialog(false)}>
            إلغاء
          </Button>
          <Button
            onClick={handleRestoreBackup}
            variant="contained"
            color="warning"
            disabled={loadingBackups}
            startIcon={loadingBackups ? <CircularProgress size={20} /> : <RestoreIcon />}
          >
            {loadingBackups ? 'جاري الاستعادة...' : 'استعادة'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار تحميل قاعدة البيانات من رابط */}
      <Dialog open={uploadFromLinkDialog} onClose={() => setUploadFromLinkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>تحميل قاعدة البيانات من رابط Dropbox</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="رابط Dropbox"
            fullWidth
            variant="outlined"
            value={dropboxLink}
            onChange={(e) => setDropboxLink(e.target.value)}
            placeholder="https://dropbox.com/s/xxxxx/backup.json?dl=0"
            sx={{ mt: 2 }}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              أدخل رابط مشاركة Dropbox لملف النسخة الاحتياطية. سيتم تحميل البيانات واستعادتها تلقائياً.
            </Typography>
          </Alert>
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>تحذير:</strong> ستؤدي هذه العملية إلى استبدال جميع البيانات الحالية!
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadFromLinkDialog(false)}>
            إلغاء
          </Button>
          <Button
            onClick={handleUploadFromLink}
            variant="contained"
            color="warning"
            disabled={!dropboxLink.trim() || loadingBackups}
            startIcon={loadingBackups ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {loadingBackups ? 'جاري التحميل...' : 'تحميل واستعادة'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار عرض رابط المشاركة */}
      <Dialog open={shareLinkDialog} onClose={() => setShareLinkDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>رابط مشاركة النسخة الاحتياطية</DialogTitle>
        <DialogContent>
          {selectedBackup && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedBackup.name}
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={4}
                value={generatedLink}
                variant="outlined"
                label="رابط المشاركة"
                sx={{ mt: 2, mb: 2 }}
                InputProps={{
                  readOnly: true,
                }}
              />

              {generatedLink.includes('dl.dropboxusercontent.com') && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>رابط تحميل مباشر!</strong> يمكنك استخدام هذا الرابط لتحميل الملف مباشرة.
                  </Typography>
                </Alert>
              )}

              {generatedLink.includes('dropbox.com/home') && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>رابط الملف في Dropbox.</strong> استخدم التعليمات المرفقة لإنشاء رابط تحميل مباشر.
                  </Typography>
                </Alert>
              )}

              {(generatedLink.includes('تعليمات') || generatedLink.includes('ملاحظة')) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>تعليمات إنشاء الرابط.</strong> يرجى اتباع التعليمات لإنشاء رابط المشاركة يدوياً.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareLinkDialog(false)}>
            إغلاق
          </Button>
          <Button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(generatedLink);
                setSnackbar({
                  open: true,
                  message: 'تم نسخ الرابط إلى الحافظة',
                  severity: 'success'
                });
              } catch (error) {
                setSnackbar({
                  open: true,
                  message: 'فشل في نسخ الرابط',
                  severity: 'error'
                });
              }
            }}
            variant="contained"
            startIcon={<LinkIcon />}
          >
            نسخ الرابط
          </Button>
          {generatedLink.includes('dl.dropboxusercontent.com') && (
            <Button
              onClick={() => window.open(generatedLink, '_blank')}
              variant="outlined"
              startIcon={<DownloadIcon />}
            >
              تحميل مباشر
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BackupPage;
